import { Controller } from "@hotwired/stimulus"
import { findStepElement, parseDataset } from "../helpers/dom.js"

// Digit keys pick an option by position. For a yes/no pair, +/− and y/n (or д/н) do the same.
export function answerIndexFromKey(event, optionCount) {
  if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return null
  if (optionCount < 1) return null

  if (/^[1-9]$/.test(event.key)) {
    const index = Number(event.key) - 1
    return index < optionCount ? index : null
  }

  if (optionCount !== 2) return null
  const key = event.key.toLowerCase()
  if (key === "+" || event.code === "NumpadAdd" || key === "y" || key === "д") return 0
  if (key === "-" || event.code === "NumpadSubtract" || key === "n" || key === "н") return 1
  return null
}

// Generic step-by-step questionnaire wizard driven by ui_schema (yes/no, true/false/cannot say).
export default class extends Controller {
  static targets = [
    "step",
    "backButton",
    "progressText",
    "progressBar",
    "form",
    "wizard",
    "consent",
    "submitButton",
    "answerButtons"
  ]
  static values = {
    total: { type: Number, default: 1 },
    allowBack: { type: Boolean, default: true },
    autoSubmit: { type: Boolean, default: true },
    requireConsent: { type: Boolean, default: false },
    answerInteraction: { type: String, default: "radio" },
    initialStep: { type: Number, default: 1 },
    autosaveUrl: { type: String, default: "" }
  }

  connect() {
    this.currentStep = this.initialStepValue > 0 ? this.initialStepValue : 1
    this.formSubmitted = false
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    this.syncConsentGate()
    this.render()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
  }

  consentChanged() {
    this.syncConsentGate()
  }

  syncConsentGate() {
    if (!this.requireConsentValue || !this.hasWizardTarget || !this.hasConsentTarget) return
    const ok = this.consentTarget.checked
    this.wizardTarget.hidden = !ok
    if (ok) {
      this.currentStep = this.initialStepValue > 0 ? this.initialStepValue : 1
      this.render()
    }
  }

  handleKeydown(event) {
    if (typeof event.target?.closest === "function" && event.target.closest("input:not([type=radio]):not([type=checkbox]), textarea, select")) return
    if (this.wizardHidden()) return

    const optionIndex = answerIndexFromKey(event, this.currentOptionCount())
    if (optionIndex != null) {
      event.preventDefault()
      this.selectOptionByIndex(optionIndex)
      return
    }

    if (this.allowBackValue && event.key === "ArrowLeft") {
      event.preventDefault()
      this.back()
      return
    }

    if (event.key === "ArrowRight" || (this.buttonInteraction() && event.key === "Enter")) {
      event.preventDefault()
      if (this.buttonInteraction() && this.currentStep >= this.totalValue) return
      this.next()
    }
  }

  answerChanged() {
    if (!this.radioInteraction()) return
    this.syncAnswerButtons()
    this.persistCurrentAnswer()
    if (!this.hasAnswer()) return
    // Enable submit button / progress before auto-submit on the last step.
    if (this.currentStep >= this.totalValue) {
      this.render()
    }
    this.next()
  }

  selectAnswer(event) {
    event.preventDefault()
    if (this.wizardHidden()) return
    const answer = event.params.answer
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    if (!stepEl) return
    const radio = stepEl.querySelector(`input[type=radio][value="${answer}"]`)
    if (!radio) return

    radio.checked = true
    this.persistCurrentAnswer()
    if (this.currentStep >= this.totalValue) {
      this.render()
    } else {
      this.next()
    }
  }

  next(event) {
    event?.preventDefault()
    if (this.wizardHidden()) return
    if (!this.hasAnswer()) return
    if (this.currentStep >= this.totalValue) {
      // Do not set formSubmitted before submit: onSubmit listens for the
      // submit event and must be able to run once; setting the flag earlier
      // caused onSubmit to preventDefault and blocked Accent final answers.
      if (this.autoSubmitValue && this.hasFormTarget && !this.formSubmitted) {
        this.submitForm()
      }
      return
    }
    this.currentStep += 1
    this.render({ announce: true })
  }

  back(event) {
    event?.preventDefault()
    if (!this.allowBackValue || this.currentStep <= 1) return
    this.currentStep -= 1
    this.render({ announce: true })
  }

  onSubmit(event) {
    if (this.wizardHidden()) {
      event.preventDefault()
      return
    }
    if (this.currentStep < this.totalValue || !this.hasAnswer()) {
      event.preventDefault()
      return
    }
    if (this.formSubmitted) {
      event.preventDefault()
      return
    }
    this.formSubmitted = true
  }

  // Wizard already enforces "current step answered"; skip HTML5 checks on hidden
  // required radios (they still fail checkValidity in Chromium) and prefer an
  // explicit submit button when present so users can retry if auto-submit stalls.
  submitForm() {
    if (!this.hasFormTarget || this.formSubmitted) return
    this.formTarget.noValidate = true
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.click()
      return
    }
    this.formTarget.requestSubmit()
  }

  hasAnswer() {
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    if (!stepEl) return false
    const name = stepEl.querySelector("input[type=radio]")?.name
    if (!name) return false
    return !!stepEl.querySelector(`input[name="${name}"]:checked`)
  }

  selectOptionByIndex(index) {
    if (this.wizardHidden()) return
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    if (!stepEl) return

    if (this.buttonInteraction()) {
      const buttons = stepEl.querySelectorAll("button[data-questionnaire-wizard-answer-param]")
      buttons[index]?.click()
      return
    }

    const radios = stepEl.querySelectorAll(".questionnaire-wizard-answer-option input[type='radio']")
    const radio = radios[index]
    if (!radio) return
    radio.checked = true
    this.answerChanged()
  }

  currentOptionCount() {
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    if (!stepEl) return 0
    if (this.buttonInteraction()) {
      return stepEl.querySelectorAll("button[data-questionnaire-wizard-answer-param]").length
    }
    return stepEl.querySelectorAll(".questionnaire-wizard-answer-option input[type='radio']").length
  }

  render({ announce = false } = {}) {
    this.stepTargets.forEach((el) => {
      const step = parseDataset(el, "step", "number")
      el.classList.toggle("is-hidden", step !== this.currentStep)
      el.hidden = step !== this.currentStep
    })

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = this.progressLabel
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.value = this.currentStep
      this.progressBarTarget.max = this.totalValue
      this.progressBarTarget.setAttribute("aria-valuenow", String(this.currentStep))
      this.progressBarTarget.setAttribute("aria-valuemax", String(this.totalValue))
    }
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.toggle("is-hidden", this.currentStep <= 1)
      this.backButtonTarget.hidden = this.currentStep <= 1
    }

    this.syncAnswerButtons()

    if (this.hasSubmitButtonTarget) {
      const onLastStep = this.currentStep >= this.totalValue
      this.submitButtonTarget.classList.toggle("is-hidden", !onLastStep)
      this.submitButtonTarget.hidden = !onLastStep
      this.submitButtonTarget.disabled = !onLastStep || !this.hasAnswer()
    }

    if (announce) this.announceCurrentQuestion()
  }

  announceCurrentQuestion() {
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    const question = stepEl?.querySelector(".questionnaire-wizard-question")
    question?.focus({ preventScroll: false })
  }

  syncAnswerButtons() {
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    if (!stepEl) return

    if (this.radioInteraction()) {
      stepEl.querySelectorAll(".questionnaire-wizard-answer-option").forEach((label) => {
        const radio = label.querySelector('input[type="radio"]')
        const selected = Boolean(radio?.checked)
        label.classList.toggle("is-primary", selected)
        label.classList.toggle("is-light", !selected)
      })
      return
    }

    const name = stepEl.querySelector("input[type=radio]")?.name
    const checkedRadio = name ? stepEl.querySelector(`input[name="${name}"]:checked`) : null
    const selectedValue = checkedRadio?.value ?? null

    stepEl.querySelectorAll("[data-questionnaire-wizard-target='answerButtons']").forEach((container) => {
      container.querySelectorAll("button[data-questionnaire-wizard-answer-param]").forEach((btn) => {
        const value = btn.dataset.questionnaireWizardAnswerParam
        btn.classList.toggle("is-primary", value === selectedValue)
        btn.classList.toggle("is-light", value !== selectedValue)
      })
    })
  }

  get progressLabel() {
    const template = this.element.querySelector("[data-questionnaire-wizard-progress-template]")
    if (template) {
      return template.textContent.replace("%{current}", this.currentStep).replace("%{total}", this.totalValue)
    }
    return `${this.currentStep} / ${this.totalValue}`
  }

  wizardHidden() {
    return this.requireConsentValue && this.hasWizardTarget && this.wizardTarget.hidden
  }

  radioInteraction() {
    return this.answerInteractionValue === "radio"
  }

  buttonInteraction() {
    return this.answerInteractionValue === "button"
  }

  persistCurrentAnswer() {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    const stepEl = findStepElement(this.stepTargets, this.currentStep)
    if (!stepEl) return
    const questionNumber = stepEl.dataset.questionNumber
    const radio = stepEl.querySelector("input[type=radio]:checked")
    if (!questionNumber || !radio) return
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    body.set("question_number", questionNumber)
    body.set("value", radio.value)
    try {
      fetch(this.autosaveUrlValue, {
        method: "POST",
        body,
        keepalive: true,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": token || "",
          "Accept": "application/json"
        }
      }).catch(() => {})
    } catch (_e) {
      // autosave must not block the next item
    }
  }
}
